import { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

type MetricValueProps = {
  value: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
};

function formatMetric(value: number, prefix: string, suffix: string, decimals: number) {
  return `${prefix}${value.toLocaleString("ru-RU", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}${suffix}`;
}

export function MetricValue({
  value,
  suffix = "",
  prefix = "",
  decimals = 0,
}: MetricValueProps) {
  const valueRef = useRef<HTMLSpanElement>(null);
  const currentValue = useRef(value);
  const hasEntered = useRef(false);
  const tween = useRef<gsap.core.Tween | null>(null);

  useLayoutEffect(() => {
    const element = valueRef.current;
    if (!element) return;

    const format = (current: number) => formatMetric(current, prefix, suffix, decimals);
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reducedMotion) {
      currentValue.current = value;
      element.textContent = format(value);
      return;
    }

    const animateToValue = (duration: number) => {
      tween.current?.kill();
      const counter = { value: currentValue.current };

      tween.current = gsap.to(counter, {
        value,
        duration,
        ease: "power3.out",
        overwrite: "auto",
        onUpdate: () => {
          currentValue.current = counter.value;
          element.textContent = format(counter.value);
        },
        onComplete: () => {
          currentValue.current = value;
          element.textContent = format(value);
        },
      });
    };

    if (hasEntered.current) {
      animateToValue(0.48);
      return () => tween.current?.kill();
    }

    currentValue.current = 0;
    element.textContent = format(0);
    const trigger = ScrollTrigger.create({
      trigger: element,
      start: "top 92%",
      once: true,
      onEnter: () => {
        hasEntered.current = true;
        animateToValue(1.15);
      },
    });

    return () => {
      trigger.kill();
      tween.current?.kill();
    };
  }, [decimals, prefix, suffix, value]);

  return <span ref={valueRef}>{formatMetric(value, prefix, suffix, decimals)}</span>;
}
